/**
 * MutationObserver-based watcher for dynamic DOM content
 *
 * Watches for new DOM nodes added after initial page load and applies
 * matching patches to them. Essential for SPAs, AJAX-loaded content,
 * lazy loading, and framework-rendered components.
 *
 * Performance considerations (F7 — this runs on customers' pages):
 * - Debounces at 100ms to batch rapid DOM mutations
 * - Only watches childList (not attributes — too noisy from React reconciliation)
 * - Skips text nodes (only processes ELEMENT_NODE additions)
 * - Checks data-helpbob-patched to prevent double-application
 * - Per-slice main-thread budget (perf-budget.json → maxMutationBatchMs,
 *   compiled in via build.mjs): when a batch exceeds it, the remaining
 *   work is deferred to requestIdleCallback (setTimeout fallback) instead
 *   of blocking the customer's frame
 * - Shadow roots are collected once per node per batch, not once per patch
 * - Patches are pre-indexed by their selector's rightmost tag/id/class so
 *   full selectors only run against nodes that can possibly match
 */

import type { PublicPatch } from '@helpbob/shared';
import type { PatchApplier } from './patch-applier';
import { readPatchMarkers } from './patch-applier';
import { collectShadowRoots } from './shadow-query';

/** Build-time define injected from perf-budget.json by build.mjs. */
declare const __HB_MUTATION_BATCH_BUDGET_MS__: number | undefined;

const BATCH_BUDGET_MS: number =
  typeof __HB_MUTATION_BATCH_BUDGET_MS__ === 'number' ? __HB_MUTATION_BATCH_BUDGET_MS__ : 8;

const now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

/**
 * Cheap prefilter derived from a selector's rightmost compound: a node
 * whose subtree can't contain the required tag/id/class is skipped without
 * running the full selector. `null` = no safe prefilter derivable — always
 * run the selector. Must never produce false negatives, so anything hard
 * to parse (selector lists, escapes, unbalanced quoting) opts out.
 */
interface SelectorPrefilter {
  tag: string | null;
  id: string | null;
  cls: string | null;
}

/**
 * Stand-in for an elided `[attr=…]` / `:pseudo(…)` block. It must NOT be a
 * combinator: dropping the block entirely would make `div > [data-x]` look
 * like it ends in `div` and prefilter against the wrong compound.
 */
const BLOCK = '\u0000';

/**
 * Quirks-mode pages match class/id selectors ASCII-case-insensitively, which
 * the cheap string comparisons below cannot reproduce — prefiltering there
 * could drop real matches, so it is disabled wholesale.
 */
const QUIRKS_MODE = typeof document !== 'undefined' && document.compatMode === 'BackCompat';

function buildPrefilter(selector: string): SelectorPrefilter | null {
  if (QUIRKS_MODE) return null;
  if (selector.includes(',')) return null; // selector list: any part may match
  // Elide [attr="…"] blocks and :pseudo(…) arguments (string-aware) so their
  // contents can't be mistaken for combinators, then take the rightmost
  // compound of what remains.
  let stripped = '';
  let brackets = 0;
  let parens = 0;
  let quote: string | null = null;
  for (let i = 0; i < selector.length; i++) {
    const ch = selector[i];
    if (quote !== null) {
      if (ch === '\\') i++;
      else if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") { quote = ch; continue; }
    if (ch === '[') { if (brackets === 0 && parens === 0) stripped += BLOCK; brackets++; continue; }
    if (ch === ']') { if (brackets === 0) return null; brackets--; continue; }
    if (ch === '(') { if (parens === 0 && brackets === 0) stripped += BLOCK; parens++; continue; }
    if (ch === ')') { if (parens === 0) return null; parens--; continue; }
    if (brackets > 0 || parens > 0) continue;
    stripped += ch;
  }
  if (brackets !== 0 || parens !== 0 || quote !== null) return null;
  if (stripped.includes('\\')) return null; // escaped identifiers: don't guess
  const parts = stripped.split(/[\s>+~]+/).filter(Boolean);
  const last = parts[parts.length - 1];
  if (!last) return null;
  // Every capture must end at a compound boundary ([#.:] , an elided block, or
  // end) — a partial capture (camelCase SVG tag like `clipPath`, non-ASCII
  // class name) would prefilter against a name fragment and drop real matches.
  // Lowercase-only tags because CSS type selectors match case-insensitively.
  const tag = last.match(/^([a-z][a-z0-9-]*)(?=[#.:\u0000]|$)/);
  const id = last.match(/#([\w-]+)(?=[#.:\u0000]|$)/);
  const cls = last.match(/\.([\w-]+)(?=[#.:\u0000]|$)/);
  // Nothing safely derivable — e.g. `*`, a bare pseudo, or a compound that is
  // attribute-only (`div > [data-x]`, whose subject may be any element).
  if (!tag && !id && !cls) return null;
  return {
    tag: tag ? tag[1] : null,
    id: id ? id[1] : null,
    cls: cls ? cls[1] : null,
  };
}

/** One added node still being processed; patchIdx tracks resume position. */
interface WorkUnit {
  el: Element;
  patchIdx: number;
  shadowRoots: ShadowRoot[] | null; // collected lazily, once per node
  /**
   * Descendants of patch `patchIdx` still awaiting application — set when a
   * slice pauses INSIDE a match list (one added container can hold
   * thousands of matches; patch-level granularity alone would let that
   * single unit blow the budget).
   */
  pendingDescendants: Element[] | null;
}

export interface MutationWatcherStats {
  /** Processing slices run (initial batches + idle continuations). */
  batches: number;
  /** Times remaining work was deferred past the budget boundary. */
  deferrals: number;
  /** Longest synchronous slice in ms. */
  maxBatchMs: number;
  /** The configured per-slice budget in ms. */
  budgetMs: number;
}

export class MutationWatcher {
  private observer: MutationObserver;
  private patches: PublicPatch[];
  private prefilters: (SelectorPrefilter | null)[] = [];
  private applier: PatchApplier;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingNodes: Set<Node> = new Set();
  private workQueue: WorkUnit[] = [];
  private continuationTimer: ReturnType<typeof setTimeout> | null = null;
  private idleHandle: number | null = null;
  private running = false;
  private stats: MutationWatcherStats = {
    batches: 0,
    deferrals: 0,
    maxBatchMs: 0,
    budgetMs: BATCH_BUDGET_MS,
  };

  constructor(patches: PublicPatch[], applier: PatchApplier) {
    this.patches = patches;
    this.prefilters = patches.map((p) => buildPrefilter(p.selector));
    this.applier = applier;
    this.observer = new MutationObserver(this.handleMutations.bind(this));
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  stop(): void {
    this.running = false;
    this.observer.disconnect();
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.cancelContinuation();
    this.pendingNodes.clear();
    this.workQueue = [];
  }

  /** Update the patch list (e.g., after SPA route change) */
  updatePatches(patches: PublicPatch[]): void {
    this.patches = patches;
    this.prefilters = patches.map((p) => buildPrefilter(p.selector));
    // Queued units index into the old patch list — restart them cleanly.
    for (const unit of this.workQueue) {
      unit.patchIdx = 0;
      unit.pendingDescendants = null;
    }
  }

  /** Get count of dynamically applied patches (for analytics) */
  getDynamicApplyCount(): number {
    return this._dynamicApplyCount;
  }

  /** Processing/budget counters (perf verification + analytics). */
  getPerfStats(): MutationWatcherStats {
    return { ...this.stats };
  }

  private _dynamicApplyCount = 0;

  private handleMutations(mutations: MutationRecord[]): void {
    for (const mutation of mutations) {
      for (let i = 0; i < mutation.addedNodes.length; i++) {
        const node = mutation.addedNodes[i];
        if (node.nodeType === Node.ELEMENT_NODE) {
          this.pendingNodes.add(node);
        }
      }
    }

    if (this.pendingNodes.size === 0) return;

    // Debounce: process after 100ms of quiet
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => this.processPendingNodes(), 100);
  }

  private processPendingNodes(): void {
    this.debounceTimer = null;
    for (const node of this.pendingNodes) {
      this.workQueue.push({
        el: node as Element,
        patchIdx: 0,
        shadowRoots: null,
        pendingDescendants: null,
      });
    }
    this.pendingNodes.clear();

    if (this.workQueue.length === 0) return;
    this.processQueue();
  }

  /**
   * Drain the work queue until done or the per-slice budget is spent; any
   * remainder is rescheduled off the critical path. Forward progress is
   * guaranteed: every slice completes at least one (node, patch) step even
   * with a budget of 0.
   */
  private processQueue(): void {
    const start = now();
    const deadline = start + BATCH_BUDGET_MS;

    while (this.workQueue.length > 0) {
      const unit = this.workQueue[0];
      if (this.processUnit(unit, deadline)) {
        this.workQueue.shift();
      }
      if (this.workQueue.length > 0 && now() >= deadline) {
        this.stats.deferrals++;
        this.scheduleContinuation();
        break;
      }
    }

    const elapsed = now() - start;
    this.stats.batches++;
    if (elapsed > this.stats.maxBatchMs) this.stats.maxBatchMs = elapsed;
  }

  /**
   * Run patches [unit.patchIdx..] against one added node. Returns true when
   * the node is fully processed, false when it paused at the deadline (the
   * resume position is stored on the unit).
   */
  private processUnit(unit: WorkUnit, deadline: number): boolean {
    const el = unit.el;
    // Removed again before we got to it (rapid framework churn) — skip.
    if (!el.isConnected) return true;

    if (unit.shadowRoots === null) {
      // One subtree walk per node — NOT one per patch (the old behavior,
      // O(patches × subtree), was the core of F7).
      unit.shadowRoots = collectShadowRoots(el);
      if (this.running) {
        for (const shadowRoot of unit.shadowRoots) {
          try {
            this.observer.observe(shadowRoot, { childList: true, subtree: true });
          } catch { /* closed/foreign root — skip */ }
        }
      }
    }

    const startIdx = unit.patchIdx;
    for (let i = startIdx; i < this.patches.length; i++) {
      // ≥1 patch of progress per call, then respect the deadline.
      if (i > startIdx && now() >= deadline) {
        unit.patchIdx = i;
        return false;
      }

      const patch = this.patches[i];
      if (patch.status !== 'active') continue;
      const prefilter = this.prefilters[i];

      let descendants: Element[] | null = null;
      if (i === startIdx && unit.pendingDescendants) {
        // Resuming inside this patch's match list — self check and the
        // subtree query already ran in the previous slice. Time has passed
        // since then, so drop elements the page detached in the meantime
        // rather than marking nodes that are no longer on the page.
        descendants = unit.pendingDescendants.filter((d) => d.isConnected);
        unit.pendingDescendants = null;
      } else {
        // Check the node itself
        try {
          if (this.selfMightMatch(el, prefilter) && el.matches?.(patch.selector)) {
            if (this.applyIfNew(el, patch)) {
              this._dynamicApplyCount++;
            }
          }
        } catch { /* invalid selector — skip */ }

        // Collect descendants (including shadow DOM)
        if (this.subtreeMightMatch(el, prefilter, unit.shadowRoots)) {
          try {
            descendants = Array.from(el.querySelectorAll(patch.selector));
            for (const shadowRoot of unit.shadowRoots) {
              descendants.push(...Array.from(shadowRoot.querySelectorAll(patch.selector)));
            }
          } catch { /* invalid selector — skip */ }
        }
      }

      if (descendants) {
        // One added container can hold thousands of matches — honor the
        // deadline INSIDE the list too (≥1 application of progress per slice)
        // and park the remainder on the unit.
        for (let d = 0; d < descendants.length; d++) {
          if (d > 0 && now() >= deadline) {
            unit.patchIdx = i;
            unit.pendingDescendants = descendants.slice(d);
            return false;
          }
          if (this.applyIfNew(descendants[d], patch)) {
            this._dynamicApplyCount++;
          }
        }
      }
    }
    return true;
  }

  private selfMightMatch(el: Element, prefilter: SelectorPrefilter | null): boolean {
    if (!prefilter) return true;
    if (prefilter.tag && el.localName.toLowerCase() !== prefilter.tag) return false;
    if (prefilter.id && el.id !== prefilter.id) return false;
    if (prefilter.cls && !el.classList.contains(prefilter.cls)) return false;
    return true;
  }

  private subtreeMightMatch(
    el: Element,
    prefilter: SelectorPrefilter | null,
    shadowRoots: ShadowRoot[],
  ): boolean {
    // Leaf node with no shadow content — nothing below to match.
    if (!el.firstElementChild && shadowRoots.length === 0) return false;
    if (!prefilter) return true;
    // Light-DOM prechecks can't see into shadow content — don't skip then.
    if (shadowRoots.length > 0) return true;
    if (prefilter.id) {
      // Probe the element's OWN root: document.getElementById cannot see into
      // shadow trees, so for a node added inside an observed shadow root it
      // would report "absent" and drop the patch. ShadowRoot inherits
      // getElementById from DocumentFragment, so both roots answer here.
      const root = el.getRootNode() as Document | ShadowRoot;
      if (typeof root.getElementById !== 'function') return true; // unknown root — don't skip
      if (!root.getElementById(prefilter.id)) return false;
    }
    // querySelector (not getElementsByTagName) for the tag probe: CSS type
    // selectors match case-insensitively, incl. camelCase SVG tag names.
    if (prefilter.tag && !el.querySelector(prefilter.tag)) return false;
    if (prefilter.cls && el.getElementsByClassName(prefilter.cls).length === 0) return false;
    return true;
  }

  private scheduleContinuation(): void {
    if (this.continuationTimer !== null || this.idleHandle !== null) return;
    const run = () => {
      this.continuationTimer = null;
      this.idleHandle = null;
      if (!this.running || this.workQueue.length === 0) return;
      this.processQueue();
    };
    if (typeof requestIdleCallback === 'function') {
      // Idle time when available; the timeout keeps patches from starving
      // on pages that never go idle.
      this.idleHandle = requestIdleCallback(run, { timeout: 200 });
    } else {
      this.continuationTimer = setTimeout(run, 30);
    }
  }

  private cancelContinuation(): void {
    if (this.continuationTimer !== null) {
      clearTimeout(this.continuationTimer);
      this.continuationTimer = null;
    }
    if (this.idleHandle !== null) {
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(this.idleHandle);
      this.idleHandle = null;
    }
  }

  /**
   * Apply a patch to an element only if it hasn't been patched with this patch ID yet.
   * Exact token match against the marker list — `includes` substring matching
   * would let e.g. an applied "patch_12" suppress "patch_1" (F5 follow-up).
   * Returns true if the patch was newly applied.
   */
  private applyIfNew(el: Element, patch: PublicPatch): boolean {
    if (readPatchMarkers(el).includes(patch.id)) return false;

    return this.applier.applyToElement(el, patch);
  }
}
